import React, { useState, useEffect } from 'react';
import { db, auth, functions } from './firebase'; // Added functions import
import { collection, onSnapshot, addDoc, serverTimestamp, query, orderBy, doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions'; // Added for secure backend calls
import { 
  FaCheckCircle, FaClock, FaTimesCircle, FaSyncAlt, FaPlus, FaTrash, 
  FaImage, FaVideo, FaFilePdf, FaPhone, FaReply, FaTimes, 
  FaExternalLinkAlt, FaMagic, FaCloudUploadAlt, FaCopy, FaSpinner, FaRocket
} from 'react-icons/fa';

// --- ULTRA MODERN ENTERPRISE THEME ---
const THEME = {
  primary: '#8b5cf6', // Vibrant Violet
  primaryGradient: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)', // Violet to Pink
  primaryLight: '#ede9fe',
  success: '#10b981', // Vivid Emerald
  successLight: '#d1fae5',
  warning: '#f59e0b', // Amber
  warningLight: '#fef3c7',
  error: '#ef4444',   // Red
  errorLight: '#fee2e2', 
  bg: '#f8fafc',      // Soft Slate
  surface: 'rgba(255, 255, 255, 0.9)', // Glassmorphism
  textMain: '#1e293b',
  textSub: '#64748b',
  border: 'rgba(226, 232, 240, 0.8)',
  accent: '#f1f5f9'
};

const safeText = (text) => {
  if (typeof text === 'string') return text;
  if (typeof text === 'number') return String(text);
  return ""; 
};

// Helper: Convert File to Base64 for Cloud Functions
const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]); // Remove data:image/png;base64,
    reader.onerror = error => reject(error);
});

function Templates() {
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showModal, setShowModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [spaCredentials, setSpaCredentials] = useState({ wabaId: "", token: "" });
  
  // --- BUILDER STATE ---
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("en_US");
  const [category, setCategory] = useState("MARKETING");
  
  const [headerType, setHeaderType] = useState("NONE"); 
  const [headerText, setHeaderText] = useState("");
  const [mediaPreview, setMediaPreview] = useState(null); 
  const [mediaFile, setMediaFile] = useState(null); 
  
  const [body, setBody] = useState("");
  const [footer, setFooter] = useState("");
  
  const [btnType, setBtnType] = useState("NONE"); 
  const [buttons, setButtons] = useState([]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- 1. DATA LOADER ---
  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u) {
        const q = query(collection(db, "businesses", u.uid, "templates"), orderBy("createdAt", "desc"));
        onSnapshot(q, (snapshot) => {
          setTemplates(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        let foundWaba = "", foundToken = "";
        const settingsSnap = await getDoc(doc(db, "businesses", u.uid, "settings", "whatsapp"));
        if (settingsSnap.exists() && settingsSnap.data().accessToken) {
            foundWaba = settingsSnap.data().accountId || settingsSnap.data().businessId || ""; 
            foundToken = settingsSnap.data().accessToken;
        } 
        if (!foundToken || !foundWaba) {
            const businessDoc = await getDoc(doc(db, "businesses", u.uid));
            if (businessDoc.exists()) {
                foundWaba = businessDoc.data().accountId || ""; 
                foundToken = businessDoc.data().accessToken || "";
            }
        }
        if (foundWaba && foundToken) setSpaCredentials({ wabaId: foundWaba, token: foundToken });
      }
    });
    return () => unsubscribeAuth();
  }, []);

  // --- MEDIA UPLOAD HANDLER ---
  const handleMediaUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        return alert("File is too large. Please select a file under 10MB.");
      }
      setMediaFile(file);
      setMediaPreview(URL.createObjectURL(file));
    }
  };

  // --- BUTTON LOGIC ---
  const addQuickReply = () => {
    if (buttons.length >= 3) return alert("Max 3 Quick Replies allowed.");
    setButtons([...buttons, { type: "QUICK_REPLY", text: "" }]);
  };

  const addCallToAction = (actionType) => {
    if (buttons.length >= 2 && actionType !== 'COPY_CODE') return alert("Max 2 Call to Actions allowed.");
    if (actionType === 'COPY_CODE' && buttons.some(b => b.type === 'COPY_CODE')) return alert("Only 1 Copy Code allowed.");
    
    setButtons([...buttons, { type: actionType, text: actionType === 'COPY_CODE' ? "Copy Offer Code" : "", value: "" }]); 
  };

  const updateButton = (index, field, value) => {
    const newButtons = [...buttons];
    newButtons[index][field] = value;
    setButtons(newButtons);
  };

  const removeButton = (index) => {
    const newButtons = [...buttons];
    newButtons.splice(index, 1);
    setButtons(newButtons);
  };

  const insertVariable = () => {
    const count = (body.match(/{{/g) || []).length + 1;
    setBody(body + ` {{${count}}} `);
  };

  // --- API ACTIONS ---
  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Delete "${templateName}" permanently?`)) return;
    setIsSaving(true);
    setLoadingText("Deleting...");
    try {
      if (spaCredentials.wabaId && spaCredentials.token) {
        await fetch(`https://graph.facebook.com/v24.0/${spaCredentials.wabaId}/message_templates?name=${templateName}`, {
          method: "DELETE",
          headers: { "Authorization": `Bearer ${spaCredentials.token}` }
        });
      }
      await deleteDoc(doc(db, "businesses", user.uid, "templates", templateId));
    } catch (error) { console.error(error); }
    setIsSaving(false);
  };

  const handleSyncTemplates = async () => {
    if (!spaCredentials.wabaId) return alert("API Not Connected");
    setIsSaving(true);
    setLoadingText("Syncing Meta...");
    try {
      const response = await fetch(`https://graph.facebook.com/v24.0/${spaCredentials.wabaId}/message_templates?limit=100`, {
        headers: { "Authorization": `Bearer ${spaCredentials.token}` }
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error.message);

      let added = 0, updated = 0;
      for (const metaTpl of data.data) {
        const langCode = metaTpl.language || "en_US"; 
        const status = metaTpl.status === "APPROVED" ? "Approved" : metaTpl.status === "REJECTED" ? "Rejected" : "Pending";
        const existing = templates.find(t => t.name === metaTpl.name && t.language === langCode);
        
        const headerComp = metaTpl.components.find(c => c.type === "HEADER");
        const bodyText = safeText(metaTpl.components.find(c => c.type === "BODY")?.text);
        const footerText = safeText(metaTpl.components.find(c => c.type === "FOOTER")?.text);

        const templateData = {
          name: metaTpl.name, category: metaTpl.category, status, language: langCode,
          body: bodyText, footer: footerText, headerType: headerComp?.format || "NONE",
          headerText: safeText(headerComp?.text), metaTemplateId: metaTpl.id, 
          createdAt: serverTimestamp()
        };

        if (!existing) {
          await addDoc(collection(db, "businesses", user.uid, "templates"), templateData);
          added++;
        } else if (existing.status !== status) {
          await updateDoc(doc(db, "businesses", user.uid, "templates", existing.id), { status });
          updated++;
        }
      }
      alert(`Sync Done! +${added} new, ${updated} updated.`);
    } catch (error) { alert(`Sync Error: ${error.message}`); }
    setIsSaving(false);
  };

  // --- REFINED SECURE UPLOAD LOGIC ---
  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!name || !body) return alert("Name and Body are required.");
    setIsSaving(true);

    let finalMediaUrl = "";
    let finalMediaHandle = ""; 

    // 🔥 THIS IS THE MAGIC FIX: Route uploads through Firebase Functions
    if (mediaFile && (headerType === 'IMAGE' || headerType === 'VIDEO' || headerType === 'DOCUMENT')) {
        
        setLoadingText("Saving to Firebase Storage...");
        try {
            const storage = getStorage();
            const ext = mediaFile.name.split('.').pop();
            const fileName = `template_media/${user.uid}/${Date.now()}.${ext}`;
            const storageRef = ref(storage, fileName);
            await uploadBytes(storageRef, mediaFile);
            finalMediaUrl = await getDownloadURL(storageRef);
        } catch (error) {
            console.error("Firebase Storage Error:", error);
        }

        setLoadingText("Uploading to Meta via Backend...");
        try {
            // Convert to base64
            const base64File = await fileToBase64(mediaFile);
            
            // Call our new Function 9
            const uploadMetaMedia = httpsCallable(functions, 'uploadMetaMedia');
            const result = await uploadMetaMedia({
                base64File: base64File,
                fileType: mediaFile.type,
                fileSize: mediaFile.size
            });
            
            finalMediaHandle = result.data.handle; 
            
        } catch (err) {
            alert(`Backend Upload Failed: ${err.message}\n\nPlease check Firebase Functions logs.`);
            setIsSaving(false);
            return;
        }
    }

    setLoadingText("Submitting Template...");
    const components = [];
    
    if (headerType !== "NONE") {
      const headerComp = { 
        type: "HEADER", 
        format: headerType === "TEXT" ? "TEXT" : headerType 
      };
      
      if (headerType === "TEXT") {
        headerComp.text = headerText;
      } else if (finalMediaHandle) {
        headerComp.example = { header_handle: [finalMediaHandle] };
      }
      components.push(headerComp);
    }

    const bodyComponent = { type: "BODY", text: body };
    const varMatches = body.match(/{{/g);
    if (varMatches) {
        const samples = varMatches.map((_, i) => `Sample ${i+1}`);
        bodyComponent.example = { body_text: [samples] };
    }
    components.push(bodyComponent);
    
    if (footer) components.push({ type: "FOOTER", text: footer });
    
    if (btnType !== "NONE" && buttons.length > 0) {
      const metaButtons = buttons.map(b => {
        if (b.type === "QUICK_REPLY") return { type: "QUICK_REPLY", text: b.text };
        if (b.type === "PHONE_NUMBER") return { type: "PHONE_NUMBER", text: b.text, phone_number: b.value };
        if (b.type === "URL") return { type: "URL", text: b.text, url: b.value };
        if (b.type === "COPY_CODE") return { type: "COPY_CODE", example: b.value };
        return null;
      }).filter(Boolean);
      components.push({ type: "BUTTONS", buttons: metaButtons });
    }

    try {
      const res = await fetch(`https://graph.facebook.com/v24.0/${spaCredentials.wabaId}/message_templates`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${spaCredentials.token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ name, language, category, components })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message);

      await addDoc(collection(db, "businesses", user.uid, "templates"), {
        name, category, language, status: "Pending", body: safeText(body),
        headerType, headerText: safeText(headerText), footer: safeText(footer),
        buttons: buttons, metaTemplateId: data.id, 
        mediaUrl: finalMediaUrl, 
        createdAt: serverTimestamp()
      });
      
      setShowModal(false);
      setName(""); setBody(""); setFooter(""); setButtons([]); setBtnType("NONE"); 
      setHeaderType("NONE"); setMediaPreview(null); setMediaFile(null);
      alert("Template Submitted Successfully!");
    } catch (error) { alert("Meta API Error: " + error.message); }
    setIsSaving(false);
  };

  return (
    <div style={{ fontFamily: '"Inter", sans-serif', background: THEME.bg, minHeight: '100vh', padding: isMobile ? '20px' : '40px' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: '20px', marginBottom: '40px', background: 'white', padding: '30px', borderRadius: '24px', boxShadow: '0 10px 30px -10px rgba(0,0,0,0.05)' }}>
        <div>
          <h1 style={{ fontSize: '36px', fontWeight: '900', color: THEME.textMain, margin: '0 0 8px 0', letterSpacing: '-1px' }}>Template <span style={{ background: THEME.primaryGradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Library</span></h1>
          <p style={{ margin: 0, color: THEME.textSub, fontSize: '16px', fontWeight: '500' }}>Design, preview, and deploy interactive Meta messages.</p>
        </div>
        <div style={{ display: 'flex', gap: '15px', width: isMobile ? '100%' : 'auto' }}>
          <button onClick={handleSyncTemplates} disabled={isSaving} style={{ flex: isMobile ? 1 : 'none', background: 'transparent', color: THEME.textMain, border: `2px solid ${THEME.border}`, padding: '16px 28px', borderRadius: '16px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', transition: 'all 0.3s' }}>
            <FaSyncAlt className={isSaving ? 'fa-spin' : ''} /> Sync Data
          </button>
          <button onClick={() => setShowModal(true)} style={{ flex: isMobile ? 1 : 'none', background: THEME.primaryGradient, color: 'white', border: 'none', padding: '16px 32px', borderRadius: '16px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 15px 25px -5px rgba(139, 92, 246, 0.4)', transition: 'all 0.3s', transform: 'translateY(-2px)' }}>
            <FaRocket /> Build New
          </button>
        </div>
      </div>

      {/* TEMPLATE GRID */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(380px, 1fr))', gap: '30px' }}>
        {templates.map(tpl => (
          <div key={tpl.id} style={{ background: 'white', borderRadius: '28px', padding: '35px', boxShadow: '0 20px 40px -15px rgba(0,0,0,0.05)', border: `1px solid ${THEME.border}`, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
            {/* Top color bar */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '6px', background: tpl.status === 'Approved' ? THEME.success : tpl.status === 'Rejected' ? THEME.error : THEME.warning }} />
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '25px' }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '900', color: THEME.primary, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>{tpl.category}</div>
                <div style={{ fontSize: '22px', fontWeight: '800', color: THEME.textMain, margin: '0 0 10px 0', wordBreak: 'break-all', lineHeight: '1.2' }}>{tpl.name}</div>
                <div style={{ fontSize: '12px', background: THEME.accent, padding: '6px 10px', borderRadius: '8px', display: 'inline-block', color: THEME.textSub, fontWeight: '700' }}>{tpl.language || 'en_US'}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-end' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '800', color: tpl.status === 'Approved' ? THEME.success : tpl.status === 'Rejected' ? THEME.error : THEME.warning, background: tpl.status === 'Approved' ? THEME.successLight : tpl.status === 'Rejected' ? THEME.errorLight : THEME.warningLight, padding: '8px 16px', borderRadius: '20px' }}>
                  {tpl.status === 'Approved' ? <FaCheckCircle /> : tpl.status === 'Rejected' ? <FaTimesCircle /> : <FaClock />} {tpl.status}
                </span>
                <button onClick={() => handleDeleteTemplate(tpl.id, tpl.name)} style={{ background: 'transparent', color: '#cbd5e1', border: 'none', cursor: 'pointer', padding: '5px', transition: 'color 0.2s' }}><FaTrash size={16} /></button>
              </div>
            </div>
            
            <div style={{ background: '#f8fafc', padding: '28px', borderRadius: '20px', border: `1px solid ${THEME.border}`, fontSize: '15px', color: '#334155', lineHeight: '1.7', flex: 1, position: 'relative' }}>
              {tpl.headerType !== 'NONE' && tpl.headerType !== 'TEXT' && (
                 <div style={{ background: 'white', color: THEME.primary, padding: '15px', borderRadius: '14px', fontSize: '14px', textAlign: 'center', marginBottom: '20px', fontWeight: '800', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    {tpl.headerType === 'IMAGE' && <FaImage size={18}/>} 
                    {tpl.headerType === 'VIDEO' && <FaVideo size={18}/>}
                    {tpl.headerType === 'DOCUMENT' && <FaFilePdf size={18}/>}
                    INCLUDES {tpl.headerType}
                 </div>
              )}
              {tpl.headerType === 'TEXT' && <div style={{ fontWeight: '900', marginBottom: '12px', color: '#0f172a', fontSize: '17px' }}>{safeText(tpl.headerText)}</div>}
              
              {safeText(tpl.body)}
              
              {tpl.footer && <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '20px', fontStyle: 'italic', fontWeight: '500' }}>{safeText(tpl.footer)}</div>}
              
              {Array.isArray(tpl.buttons) && tpl.buttons.length > 0 && (
                <div style={{ marginTop: '25px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {tpl.buttons.map((b, idx) => (
                    <span key={idx} style={{ fontSize: '13px', background: 'white', border: `1px solid ${THEME.primaryLight}`, padding: '10px 16px', borderRadius: '12px', color: THEME.primary, fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        {b.type === 'PHONE_NUMBER' && <FaPhone/>}
                        {b.type === 'URL' && <FaExternalLinkAlt/>}
                        {b.type === 'QUICK_REPLY' && <FaReply/>}
                        {b.type === 'COPY_CODE' && <FaCopy/>}
                        {safeText(b.text)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* --- ULTRA MODERN BUILDER MODAL --- */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(16px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '0px' : '40px' }}>
          <div style={{ background: THEME.surface, width: '100%', maxWidth: '1400px', height: isMobile ? '100vh' : '92vh', borderRadius: isMobile ? '0px' : '36px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.2)' }}>
            
            {/* LEFT: FORM CONFIGURATOR */}
            <div style={{ flex: 1.3, display: 'flex', flexDirection: 'column', background: 'white', borderRight: isMobile ? 'none' : `1px solid ${THEME.border}`, overflowY: 'auto' }}>
              
              {/* Modal Header */}
              <div style={{ padding: isMobile ? '25px' : '40px 50px', borderBottom: `1px solid ${THEME.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', zIndex: 10 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: isMobile ? '24px' : '32px', fontWeight: '900', color: THEME.textMain, letterSpacing: '-1px' }}>Studio Creator</h2>
                  <p style={{ margin: '8px 0 0 0', color: THEME.primary, fontSize: '15px', fontWeight: '700' }}>Secured by Firebase Functions</p>
                </div>
                <button onClick={() => setShowModal(false)} style={{ background: THEME.accent, border: 'none', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: THEME.textSub, transition: 'background 0.2s' }}><FaTimes size={22} /></button>
              </div>

              <form onSubmit={handleSaveTemplate} style={{ padding: isMobile ? '25px' : '50px' }}>
                
                {/* Section 1: Details */}
                <div style={sectionStyle}>
                  <h3 style={sectionTitleStyle}><span style={numberBadgeStyle}>1</span> Core Setup</h3>
                  <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '30px' }}>
                    <div style={{ flex: 2 }}>
                      <label style={labelStyle}>Template Name (Lowercase & Underscores)</label>
                      <input required placeholder="e.g. exclusive_vip_invite" value={name} onChange={e => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} style={inputStyle} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Category</label>
                      <select value={category} onChange={e => setCategory(e.target.value)} style={inputStyle}>
                        <option value="MARKETING">Marketing</option>
                        <option value="UTILITY">Utility</option>
                        <option value="AUTHENTICATION">Authentication</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section 2: Header */}
                <div style={sectionStyle}>
                  <h3 style={sectionTitleStyle}><span style={numberBadgeStyle}>2</span> Visual Header <span style={{fontWeight:'500', color:THEME.textSub}}>(Optional)</span></h3>
                  <select value={headerType} onChange={e => { setHeaderType(e.target.value); setMediaPreview(null); setMediaFile(null); }} style={{ ...inputStyle, marginBottom: '25px' }}>
                    <option value="NONE">Clean (No Header)</option>
                    <option value="TEXT">Bold Text Header</option>
                    <option value="IMAGE">Hero Image</option>
                    <option value="VIDEO">Dynamic Video</option>
                    <option value="DOCUMENT">PDF Document</option>
                  </select>
                  
                  {headerType === 'TEXT' && <input maxLength="60" placeholder="Attention grabbing headline..." value={headerText} onChange={e => setHeaderText(e.target.value)} style={inputStyle} />}
                  
                  {(headerType === 'IMAGE' || headerType === 'VIDEO' || headerType === 'DOCUMENT') && (
                    <div style={{ background: THEME.bg, padding: '40px', borderRadius: '24px', border: `2px dashed #cbd5e1`, textAlign: 'center', transition: 'border 0.2s' }}>
                      <input type="file" id="media-upload" accept={headerType === 'IMAGE' ? "image/*" : headerType === 'VIDEO' ? "video/*" : ".pdf"} hidden onChange={handleMediaUpload} />
                      
                      <label htmlFor="media-upload" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', gap: '15px' }}>
                        {!mediaPreview ? (
                            <>
                              <div style={{ width: '80px', height: '80px', borderRadius: '24px', background: THEME.primaryGradient, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', boxShadow: '0 10px 15px -3px rgba(139, 92, 246, 0.3)' }}>
                                <FaCloudUploadAlt size={40} />
                              </div>
                              <span style={{ fontSize: '18px', fontWeight: '900', color: THEME.textMain, marginTop: '10px' }}>Select Media File</span>
                              <span style={{ fontSize: '14px', color: THEME.textSub, fontWeight: '600' }}>Securely uploaded via Backend</span>
                            </>
                        ) : (
                            <div style={{ width: '100%', background: 'white', padding: '25px', borderRadius: '16px', border: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', overflow: 'hidden' }}>
                                    <div style={{ width: '55px', height: '55px', borderRadius: '16px', background: THEME.successLight, color: THEME.success, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <FaCheckCircle size={28} />
                                    </div>
                                    <div style={{ textAlign: 'left', overflow: 'hidden' }}>
                                        <div style={{ fontSize: '16px', fontWeight: '900', color: THEME.textMain, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px' }}>{mediaFile?.name}</div>
                                        <div style={{ fontSize: '14px', color: THEME.success, fontWeight: '700' }}>Ready for processing</div>
                                    </div>
                                </div>
                                <span style={{ color: THEME.primary, fontSize: '14px', fontWeight: '900', background: THEME.primaryLight, padding: '10px 18px', borderRadius: '12px' }}>Change File</span>
                            </div>
                        )}
                      </label>
                    </div>
                  )}
                </div>

                {/* Section 3: Body */}
                <div style={sectionStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{...sectionTitleStyle, margin: 0}}><span style={numberBadgeStyle}>3</span> Main Content</h3>
                    <button type="button" onClick={insertVariable} style={{ fontSize: '14px', background: 'white', color: THEME.primary, border: `2px solid ${THEME.border}`, padding: '10px 18px', borderRadius: '12px', cursor: 'pointer', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}><FaMagic /> Add Variable</button>
                  </div>
                  <textarea required rows="7" placeholder="Write your compelling message here. Use variables like {{1}} for personalized names or dates." value={body} onChange={e => setBody(e.target.value)} style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical', fontSize: '16px', lineHeight: '1.7', padding: '20px' }} />
                </div>

                {/* Section 4: Footer */}
                <div style={sectionStyle}>
                  <h3 style={sectionTitleStyle}><span style={numberBadgeStyle}>4</span> Footer <span style={{fontWeight:'500', color:THEME.textSub}}>(Optional)</span></h3>
                  <input maxLength="60" placeholder="e.g. Reply STOP to opt out" value={footer} onChange={e => setFooter(e.target.value)} style={inputStyle} />
                </div>

                {/* Section 5: Buttons */}
                <div style={{ marginBottom: isMobile ? '140px' : '80px' }}>
                  <h3 style={sectionTitleStyle}><span style={numberBadgeStyle}>5</span> Interactive Buttons</h3>
                  <div style={{ display: 'flex', gap: '15px', marginBottom: '30px', flexWrap: 'wrap' }}>
                    {['NONE', 'QUICK_REPLY', 'CALL_TO_ACTION'].map(type => (
                      <button key={type} type="button" onClick={() => { setBtnType(type); setButtons([]); }} 
                        style={{ flex: 1, padding: '18px', borderRadius: '16px', border: btnType === type ? `2px solid ${THEME.primary}` : `2px solid ${THEME.border}`, background: btnType === type ? THEME.primaryLight : 'white', color: btnType === type ? THEME.primary : THEME.textSub, fontWeight: '900', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s', minWidth: '140px' }}>
                        {type.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>

                  {btnType === 'QUICK_REPLY' && (
                    <div style={{ background: THEME.bg, padding: '30px', borderRadius: '24px', border: `1px solid ${THEME.border}` }}>
                      {buttons.map((btn, i) => (
                        <div key={i} style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                          <input placeholder="Button Text (e.g. Yes, please!)" value={btn.text} onChange={e => updateButton(i, 'text', e.target.value)} maxLength="25" style={inputStyle} />
                          <button type="button" onClick={() => removeButton(i)} style={{ color: THEME.error, background: 'white', border: `1px solid ${THEME.border}`, borderRadius: '16px', padding: '0 25px', cursor: 'pointer', transition: 'all 0.2s' }}><FaTrash size={18}/></button>
                        </div>
                      ))}
                      {buttons.length < 3 && <button type="button" onClick={addQuickReply} style={addBtnStyle}>+ Add Quick Reply ({3 - buttons.length} left)</button>}
                    </div>
                  )}

                  {btnType === 'CALL_TO_ACTION' && (
                    <div style={{ background: THEME.bg, padding: '30px', borderRadius: '24px', border: `1px solid ${THEME.border}` }}>
                      {buttons.map((btn, i) => (
                        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '25px', background: 'white', padding: '25px', borderRadius: '20px', border: `1px solid ${THEME.border}`, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontSize: '14px', fontWeight: '900', color: THEME.primary, textTransform: 'uppercase', letterSpacing: '1px' }}>{btn.type.replace('_', ' ')}</div>
                            <button type="button" onClick={() => removeButton(i)} style={{ color: THEME.error, border: 'none', background: THEME.errorLight, padding: '10px', borderRadius: '10px', cursor: 'pointer' }}><FaTrash size={16}/></button>
                          </div>
                          
                          {btn.type === 'COPY_CODE' ? (
                              <input placeholder="Offer Code (e.g. VIP2026)" value={btn.value} onChange={e => updateButton(i, 'value', e.target.value)} maxLength="15" style={inputStyle} />
                          ) : (
                              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px' }}>
                                <input placeholder="Label (e.g. Call Us)" value={btn.text} onChange={e => updateButton(i, 'text', e.target.value)} maxLength="20" style={{ ...inputStyle, flex: 1 }} />
                                <input placeholder={btn.type === 'PHONE_NUMBER' ? '+91...' : 'https://...'} value={btn.value} onChange={e => updateButton(i, 'value', e.target.value)} style={{ ...inputStyle, flex: 1.5 }} />
                              </div>
                          )}
                        </div>
                      ))}
                      
                      <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                        {buttons.length < 2 && <button type="button" onClick={() => addCallToAction('PHONE_NUMBER')} style={addBtnStyle}>+ Add Phone</button>}
                        {buttons.length < 2 && <button type="button" onClick={() => addCallToAction('URL')} style={addBtnStyle}>+ Add Link</button>}
                        {buttons.length < 2 && !buttons.some(b => b.type === 'COPY_CODE') && <button type="button" onClick={() => addCallToAction('COPY_CODE')} style={addBtnStyle}>+ Add Copy Code</button>}
                      </div>
                    </div>
                  )}
                </div>

                {/* Fixed Bottom Submit Bar */}
                <div style={{ display: 'flex', gap: '20px', position: 'fixed', bottom: 0, left: 0, right: isMobile ? 0 : '42%', background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(15px)', padding: '25px 50px', borderTop: `1px solid ${THEME.border}`, zIndex: 50, boxShadow: '0 -10px 25px -5px rgba(0,0,0,0.05)' }}>
                  <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: '20px', background: 'white', border: `2px solid ${THEME.border}`, borderRadius: '20px', fontWeight: '900', fontSize: '16px', color: THEME.textSub, cursor: 'pointer' }}>Cancel</button>
                  <button type="submit" disabled={isSaving} style={{ flex: 2, padding: '20px', background: isSaving ? '#a78bfa' : THEME.primaryGradient, border: 'none', borderRadius: '20px', fontWeight: '900', fontSize: '18px', color: 'white', cursor: isSaving ? 'not-allowed' : 'pointer', boxShadow: '0 15px 25px -5px rgba(139, 92, 246, 0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', transition: 'all 0.3s' }}>
                    {isSaving ? <><FaSpinner className="fa-spin" size={24} /> {loadingText}</> : 'Push to Production'}
                  </button>
                </div>
              </form>
            </div>

            {/* RIGHT: LIVE DEVICE PREVIEW */}
            {!isMobile && (
              <div style={{ flex: 0.9, background: '#e2e8f0', backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '20px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', padding: '40px 20px', borderLeft: `1px solid ${THEME.border}` }}>
                <div style={{ background: 'white', padding: '12px 24px', borderRadius: '30px', fontWeight: '900', color: THEME.primary, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '40px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>Live Rendering</div>
                
                <div style={{ width: '360px', height: '740px', background: '#0f172a', borderRadius: '60px', padding: '16px', boxShadow: '0 40px 80px -20px rgba(0,0,0,0.5)', border: '4px solid #334155', position: 'relative' }}>
                  {/* Notch */}
                  <div style={{position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)', width: '120px', height: '30px', background: '#0f172a', borderBottomLeftRadius: '20px', borderBottomRightRadius: '20px', zIndex: 20}}></div>

                  <div style={{ width: '100%', height: '100%', background: '#efeae2', borderRadius: '44px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    
                    {/* WhatsApp Header Mock */}
                    <div style={{ height: '90px', background: '#008069', display: 'flex', alignItems: 'flex-end', padding: '0 20px 15px 20px', color: 'white', fontWeight: '600', gap: '15px' }}>
                       <div style={{width:'40px', height:'40px', borderRadius:'50%', background:'white', display:'flex', alignItems:'center', justifyContent:'center', color:'#008069', fontSize:'14px', fontWeight:'900'}}>Logo</div>
                       <div>
                          <div style={{fontSize:'17px', lineHeight:'1.2'}}>Business Name</div>
                          <div style={{fontSize:'13px', opacity:0.8}}>bot</div>
                       </div>
                    </div>

                    {/* Chat Area */}
                    <div style={{ flex: 1, padding: '20px 15px', backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")', backgroundSize: 'cover', overflowY: 'auto' }}>
                      
                      <div style={{ background: 'white', borderRadius: '0 18px 18px 18px', padding: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', maxWidth: '92%' }}>
                        
                        {/* PREVIEW MEDIA HEADER */}
                        {headerType === 'IMAGE' && (
                          <div style={{ height: '180px', background: '#e2e8f0', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: '10px' }}>
                            {mediaPreview ? <img src={mediaPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <FaImage size={45} color="#cbd5e1" />}
                          </div>
                        )}
                        {headerType === 'VIDEO' && (
                          <div style={{ height: '180px', background: '#e2e8f0', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: '10px', position: 'relative' }}>
                            {mediaPreview ? <video src={mediaPreview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <FaVideo size={45} color="#cbd5e1" />}
                            <div style={{position:'absolute', width:'55px', height:'55px', background:'rgba(0,0,0,0.6)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', color:'white', backdropFilter: 'blur(4px)'}}><FaPlus size={22} style={{transform:'rotate(45deg)'}}/></div>
                          </div>
                        )}
                        {headerType === 'DOCUMENT' && (
                          <div style={{ height: '80px', background: '#e2e8f0', borderRadius: '14px', display: 'flex', alignItems: 'center', padding: '0 15px', marginBottom: '10px', gap: '15px' }}>
                            <div style={{width:'50px', height:'50px', background:'#ef4444', borderRadius:'12px', display:'flex', alignItems:'center', justifyContent:'center', color:'white'}}><FaFilePdf size={24} /></div>
                            <div>
                               <div style={{fontSize:'15px', fontWeight:'800', color:'#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '170px'}}>{mediaFile ? mediaFile.name : 'document.pdf'}</div>
                               <div style={{fontSize:'13px', color:'#94a3b8'}}>{mediaFile ? (mediaFile.size / 1024 / 1024).toFixed(1) + ' MB' : '1.2 MB'}</div>
                            </div>
                          </div>
                        )}
                        {headerType === 'TEXT' && headerText && <div style={{ fontWeight: '900', padding: '10px 10px 5px', fontSize: '17px', color: '#111b21' }}>{safeText(headerText)}</div>}

                        {/* PREVIEW BODY */}
                        <div style={{ padding: '8px 10px', fontSize: '16px', lineHeight: '1.5', color: '#111b21', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {safeText(body) || "Start typing to see your message..."}
                        </div>

                        {/* PREVIEW FOOTER */}
                        {footer && <div style={{ padding: '0 10px', fontSize: '13px', color: '#667781', marginTop: '8px' }}>{safeText(footer)}</div>}
                        <div style={{ textAlign: 'right', fontSize: '12px', color: '#667781', padding: '5px 10px 8px 0' }}>10:45 AM</div>
                      </div>

                      {/* PREVIEW BUTTONS */}
                      <div style={{ marginTop: '8px', maxWidth: '92%' }}>
                        {buttons.map((btn, i) => (
                          <div key={i} style={{ background: 'white', borderRadius: '12px', padding: '14px', marginTop: '8px', textAlign: 'center', color: '#00a884', fontWeight: '700', fontSize: '16px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                            {btn.type === 'PHONE_NUMBER' && <FaPhone size={18}/>}
                            {btn.type === 'URL' && <FaExternalLinkAlt size={18}/>}
                            {btn.type === 'QUICK_REPLY' && <FaReply size={18}/>}
                            {btn.type === 'COPY_CODE' && <FaCopy size={18}/>}
                            {btn.type === 'COPY_CODE' ? "Copy Offer Code" : safeText(btn.text) || "Button Label"}
                          </div>
                        ))}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
}

// --- SHARED STYLES ---
const sectionStyle = { marginBottom: '50px', paddingBottom: '50px', borderBottom: `1px solid ${THEME.border}` };
const sectionTitleStyle = { margin: '0 0 25px 0', fontSize: '20px', fontWeight: '900', color: THEME.textMain, letterSpacing: '-0.5px', display: 'flex', alignItems: 'center', gap: '15px' };
const numberBadgeStyle = { background: THEME.primaryLight, color: THEME.primary, width: '32px', height: '32px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' };
const labelStyle = { display: 'block', fontSize: '14px', fontWeight: '900', textTransform: 'uppercase', color: THEME.textSub, marginBottom: '12px', letterSpacing: '0.5px' };
const inputStyle = { width: '100%', padding: '18px 22px', borderRadius: '16px', border: `2px solid ${THEME.border}`, fontSize: '16px', outline: 'none', background: THEME.bg, transition: 'all 0.2s', boxSizing: 'border-box', color: THEME.textMain, fontWeight: '500' };
const addBtnStyle = { display: 'inline-block', fontSize: '15px', color: THEME.textMain, background: 'white', border: `2px dashed ${THEME.border}`, padding: '14px 24px', borderRadius: '14px', fontWeight: '900', cursor: 'pointer', transition: 'all 0.2s' };

export default Templates;